import type { DocCategory } from "../../views/documentCentre/categories";
import { baseApi } from "./baseApi";
import { NEVER_RETRIED } from "./noRetry";

/**
 * The instance's connection to the customer's own SharePoint, and the caller's
 * own Microsoft sign-in. Candidate documents live in SharePoint and are read as
 * the signed-in person, so both have to be in place before any file is listed.
 * Nothing here ever carries a token.
 */

/** One site the customer's IT granted, as an admin added it here. */
export interface SharePointSite {
  id: string;
  siteUrl: string;
  displayName: string | null;
  /** The last time someone tried to open it, and what happened. */
  check: "unchecked" | "can_open" | "cannot_open" | "bad_address";
  checkedAt: string | null;
  /** The exact text for IT, with the app id and this site filled in. */
  grantSnippet: string;
}

/** Where a new candidate folder is created unless someone picks another parent. */
export interface SharePointDefaultLocation {
  siteId: string;
  driveId: string;
  itemId: string;
  path: string;
  webUrl: string;
}

/** What an admin can say on the candidate's upload page. Null is "use the default". */
export interface CandidatePageFields {
  companyName: string | null;
  introMessage: string | null;
  consentText: string | null;
  privacyUrl: string | null;
  supportEmail: string | null;
}

export interface CandidatePageSettings {
  /** What was saved here. */
  settings: CandidatePageFields;
  /** What a candidate sees: the saved value, or the default, with {company} filled in. */
  effective: CandidatePageFields;
  /** What is used when nothing is saved. {company} is still written out in these. */
  defaults: CandidatePageFields;
  /**
   * No company name is set. Candidates then see no name at all, and "the
   * hiring team" in every sentence: never the name skyHire has on record for
   * the organisation. (The field keeps its older name.)
   */
  companyNameIsDefault: boolean;
  /**
   * The company's logo and the colour it was made to sit on. Not settings of
   * this card (they come from the careers page), but the preview shows them.
   */
  logoUrl?: string | null;
  headerColor?: string | null;
  /** The portal's accent: the stripe along the page's top edge. Null when it has none. */
  accentColor?: string | null;
  /** As on DocumentSettings: what was read, sent back with a save. */
  version?: string;
}

/** One thing this organisation collects from everyone it hires. */
export interface ChecklistItem {
  /** Null for an item not saved yet: the server gives it an id, which never changes after. */
  id: string | null;
  label: string;
  category: DocCategory;
  required: boolean;
}

/** The organisation's document checklist, and who else hears when a candidate submits. */
export interface DocumentSettings {
  /**
   * Names the settings as they were read. It goes back unchanged with a save,
   * so the server can tell that someone else saved in between and refuse
   * instead of overwriting them. Absent from a server that predates it.
   */
  version?: string;
  /** At most 20, in the order they are shown everywhere. */
  checklist: ChecklistItem[];
  /** At most 5 addresses, besides the person who sent the request. */
  submissionNotifyEmails: string[];
}

export interface SharePointStatus {
  /**
   * False when the feature is switched off for this instance. Then it does not
   * exist here: no menu item, no settings card, no Documents tab, and no call
   * to any other route of it (they all answer 404 FEATURE_NOT_ENABLED). Every
   * reader of this object checks it first. Absent from an older answer, which
   * means on.
   */
  available?: boolean;
  /** False until the deployment has credentials; then nothing else here can be acted on. */
  configured: boolean;
  /**
   * Connected, with at least one usable site: folders can be created and
   * linked. The Document Centre gates on this, never on `sites`, which is empty
   * for anyone who cannot manage. It does not imply a default location.
   */
  ready: boolean;
  /**
   * Whether an admin chose where new candidate folders go. It is optional, and
   * everyone can see this flag: without one, "Create folder" asks where first.
   */
  hasDefaultLocation: boolean;
  /** The caller holds integrations.manage. */
  canManage: boolean;
  /**
   * Whether the caller's role lets them open candidate documents at all
   * (documents.view), and whether it lets them change anything: folders,
   * uploads, requests, reviews, deleting records (documents.manage).
   *
   * One rule, for good: a boolean is obeyed exactly, and an answer WITHOUT the
   * field is from a server that predates it and means what it meant then,
   * which is allowed. A server that knows the roles always sends both, so the
   * tolerant case cannot arise between a client and server deployed together.
   * And the server refuses regardless: these decide what is drawn (the menu
   * item, the page, a checkbox), never what is permitted. What may be done
   * with one request still comes only from that request's `actions`.
   */
  canView?: boolean;
  canManageDocuments?: boolean;
  org: {
    connected: boolean;
    directoryId: string | null;
    connectedBy: string | null;
    connectedAt: string | null;
  };
  /** Empty, and the default location null, for anyone who cannot manage. */
  sites: SharePointSite[];
  defaultLocation: SharePointDefaultLocation | null;
  /** reauth_required = Microsoft refused the refresh; they have to sign in again. */
  me: { status: "none" | "active" | "reauth_required"; accountEmail: string | null };
}

export interface SharePointBrowseCrumb {
  label: string;
  siteId?: string;
  driveId?: string;
  itemId?: string;
}

export interface SharePointBrowseEntry {
  kind: "site" | "drive" | "folder";
  name: string;
  webUrl: string;
  siteId: string;
  driveId?: string;
  itemId?: string;
  childCount?: number;
}

/** One level of the folder picker: sites, then a site's libraries, then folders. */
export interface SharePointBrowse {
  level: "sites" | "drives" | "items";
  breadcrumb: SharePointBrowseCrumb[];
  entries: SharePointBrowseEntry[];
  /** The server stopped reading before the end: more exist than `entries` shows. */
  truncated: boolean;
}

/** connect = an admin connecting the organisation; signin = one person's own sign-in. */
export type SharePointAuthPurpose = "connect" | "signin";

/** `siteId` is the skyHire id of a chosen site (SharePointSite.id), and goes with every driveId. */
export interface SharePointBrowseArgs {
  instanceId: string;
  siteId?: string;
  driveId?: string;
  itemId?: string;
}

/** Leaves out what was not asked for, so the server never reads an empty id as a real one. */
function definedParams(params: Record<string, string | number | undefined | null>) {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") out[key] = value;
  }
  return out;
}

export const sharepointApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSharePointStatus: builder.query<SharePointStatus, { instanceId: string }>({
      query: ({ instanceId }) => ({
        url: "/sharepoint/status",
        method: "GET",
        params: { instanceId },
      }),
      providesTags: ["SharePoint"],
    }),

    /** The link an admin sends to the customer's IT to approve the app. */
    getSharePointApprovalLink: builder.query<{ url: string }, { instanceId: string }>({
      query: ({ instanceId }) => ({
        url: "/sharepoint/approval-link",
        method: "GET",
        params: { instanceId },
      }),
    }),

    /** Microsoft's sign-in page for the caller; it comes back to `returnTo`. */
    getSharePointAuthUrl: builder.query<
      { authUrl: string },
      { instanceId: string; purpose: SharePointAuthPurpose; returnTo?: string }
    >({
      query: ({ instanceId, purpose, returnTo }) => ({
        url: "/sharepoint/auth-url",
        method: "GET",
        params: definedParams({ instanceId, purpose, returnTo }),
      }),
    }),

    addSharePointSite: builder.mutation<SharePointSite, { instanceId: string; siteUrl: string }>({
      extraOptions: NEVER_RETRIED,
      query: (body) => ({ url: "/sharepoint/sites", method: "POST", body }),
      invalidatesTags: ["SharePoint", "SharePointBrowse"],
    }),

    /** Opens the site as the caller and records whether that worked. */
    checkSharePointSite: builder.mutation<SharePointSite, { instanceId: string; id: string }>({
      extraOptions: NEVER_RETRIED,
      query: ({ instanceId, id }) => ({
        url: `/sharepoint/sites/${encodeURIComponent(id)}/check`,
        method: "POST",
        body: { instanceId },
      }),
      invalidatesTags: ["SharePoint", "SharePointBrowse"],
    }),

    removeSharePointSite: builder.mutation<
      { success: boolean },
      { instanceId: string; id: string }
    >({
      extraOptions: NEVER_RETRIED,
      query: ({ instanceId, id }) => ({
        url: `/sharepoint/sites/${encodeURIComponent(id)}`,
        method: "DELETE",
        params: { instanceId },
      }),
      invalidatesTags: ["SharePoint", "SharePointBrowse"],
    }),

    setSharePointDefaultLocation: builder.mutation<
      SharePointDefaultLocation,
      { instanceId: string; siteId: string; driveId: string; itemId: string }
    >({
      extraOptions: NEVER_RETRIED,
      query: (body) => ({ url: "/sharepoint/default-location", method: "PUT", body }),
      invalidatesTags: ["SharePoint"],
    }),

    /**
     * Signs everyone out of Microsoft here. The chosen sites, the default
     * location and every candidate's folder link are kept, so connecting again
     * picks up where it left off.
     *
     * `reset` also removes the sites, the default location and every folder
     * link, and cancels the open document requests. Nothing is deleted in
     * SharePoint either way.
     */
    disconnectSharePoint: builder.mutation<
      { success: boolean },
      { instanceId: string; reset?: boolean }
    >({
      extraOptions: NEVER_RETRIED,
      query: ({ instanceId, reset }) => ({
        url: "/sharepoint/connection",
        method: "DELETE",
        params: reset ? { instanceId, reset: "true" } : { instanceId },
      }),
      invalidatesTags: [
        "SharePoint",
        "SharePointBrowse",
        "CandidateDocumentPeople",
        "CandidateDocumentFolder",
        "CandidateDocumentRequests",
      ],
    }),

    /** Removes the caller's own sign-in and nobody else's. */
    signOutOfSharePoint: builder.mutation<{ success: boolean }, { instanceId: string }>({
      extraOptions: NEVER_RETRIED,
      query: ({ instanceId }) => ({
        url: "/sharepoint/me",
        method: "DELETE",
        params: { instanceId },
      }),
      invalidatesTags: ["SharePoint", "SharePointBrowse", "CandidateDocumentFolder"],
    }),

    /** The document checklist and the submission notification addresses. Admins only. */
    getDocumentSettings: builder.query<DocumentSettings, { instanceId: string }>({
      query: ({ instanceId }) => ({
        url: "/sharepoint/document-settings",
        method: "GET",
        params: { instanceId },
      }),
    }),

    /**
     * The checklist alone, for the people who ask for documents and upload
     * them: the settings above are for admins. Empty when none is set.
     */
    getDocumentChecklist: builder.query<{ checklist: ChecklistItem[] }, { instanceId: string }>({
      query: ({ instanceId }) => ({
        url: "/candidate-documents/checklist",
        method: "GET",
        params: { instanceId },
      }),
    }),

    /** Saves both parts at once. The answer is what was kept, new items with their ids. */
    saveDocumentSettings: builder.mutation<
      DocumentSettings,
      { instanceId: string } & DocumentSettings
    >({
      extraOptions: NEVER_RETRIED,
      query: ({ instanceId, ...settings }) => ({
        url: "/sharepoint/document-settings",
        method: "PUT",
        params: { instanceId },
        body: settings,
      }),
      // What counts as missing follows the checklist: for the people list and
      // its counts, and for the checklist beside every candidate's folder.
      invalidatesTags: ["CandidateDocumentPeople", "CandidateDocumentFolder"],
      async onQueryStarted({ instanceId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(sharepointApi.util.upsertQueryData("getDocumentSettings", { instanceId }, data));
          // The same checklist as the dialogs read it, where one of them has.
          dispatch(
            sharepointApi.util.updateQueryData("getDocumentChecklist", { instanceId }, (draft) => {
              draft.checklist = data.checklist;
            }),
          );
        } catch {
          // The form says why; what was shown before stays as it was.
        }
      },
    }),

    /** What candidates read on their upload page. Admins only. */
    getCandidatePageSettings: builder.query<CandidatePageSettings, { instanceId: string }>({
      query: ({ instanceId }) => ({
        url: "/sharepoint/candidate-page",
        method: "GET",
        params: { instanceId },
      }),
    }),

    /**
     * Saves the five fields. A field sent as null goes back to its default.
     * The answer is the whole object again, so the form shows what was kept.
     */
    saveCandidatePageSettings: builder.mutation<
      CandidatePageSettings,
      { instanceId: string; version?: string } & CandidatePageFields
    >({
      extraOptions: NEVER_RETRIED,
      query: ({ instanceId, ...fields }) => ({
        url: "/sharepoint/candidate-page",
        method: "PUT",
        params: { instanceId },
        body: fields,
      }),
      async onQueryStarted({ instanceId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            sharepointApi.util.upsertQueryData("getCandidatePageSettings", { instanceId }, data),
          );
        } catch {
          // The form says why; what was shown before stays as it was.
        }
      },
    }),

    /**
     * No ids lists the chosen sites; a site lists its libraries; a library (and
     * optionally a folder in it) lists the folders inside. Folders only.
     */
    browseSharePoint: builder.query<SharePointBrowse, SharePointBrowseArgs>({
      query: ({ instanceId, siteId, driveId, itemId }) => ({
        url: "/sharepoint/browse",
        method: "GET",
        params: definedParams({ instanceId, siteId, driveId, itemId }),
      }),
      providesTags: ["SharePointBrowse"],
    }),
  }),
});

export const {
  useGetSharePointStatusQuery,
  useGetSharePointApprovalLinkQuery,
  useLazyGetSharePointAuthUrlQuery,
  useAddSharePointSiteMutation,
  useCheckSharePointSiteMutation,
  useRemoveSharePointSiteMutation,
  useSetSharePointDefaultLocationMutation,
  useDisconnectSharePointMutation,
  useSignOutOfSharePointMutation,
  useBrowseSharePointQuery,
  useGetCandidatePageSettingsQuery,
  useSaveCandidatePageSettingsMutation,
  useGetDocumentSettingsQuery,
  useGetDocumentChecklistQuery,
  useSaveDocumentSettingsMutation,
} = sharepointApi;
